import { API_BASE_URL } from '../config/api';
import { HELPORT_MARK_SVG, getHelportMarkFaviconDataUri } from '../components/HelportMarkIcon';

const escapeHtml = (value) => (
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const VIEWER_STYLES = `
  body{margin:0;background:#0d1117;color:#fff;font-family:Inter,Arial,sans-serif;}
  .attachment-viewer-header{display:flex;align-items:center;gap:12px;padding:12px 20px;background:#060912;border-bottom:1px solid rgba(0,210,127,.25);}
  .attachment-viewer-header__mark{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
  .attachment-viewer-header span{font-size:.92rem;color:#00d27f;font-weight:600;}
  .attachment-viewer-content{display:flex;align-items:center;justify-content:center;min-height:calc(100vh - 57px);}
  .attachment-viewer-frame{width:100vw;height:calc(100vh - 57px);border:none;background:#fff;}
  .attachment-viewer-content img{max-width:100%;max-height:calc(100vh - 57px);object-fit:contain;}
`;

export const buildAttachmentUrl = (filename, displayName = '') => {
  if (!filename) return null;
  if (filename.startsWith('inline:')) {
    return filename.slice('inline:'.length);
  }
  if (/^data:/i.test(filename)) return filename;
  if (/^https?:\/\//i.test(filename)) return filename;
  if (filename.startsWith('/')) return `${API_BASE_URL}${filename}`;

  const safeName = filename.split(/[/\\]/).pop();
  const params = new URLSearchParams({
    action: 'view_file',
    file: safeName,
  });
  if (displayName) {
    params.set('title', displayName);
  }
  return `${API_BASE_URL}/api.php?${params.toString()}`;
};

export const getAttachmentViewerTitle = (displayName = 'Document') => {
  const text = String(displayName || '').trim() || 'Document';
  const shortLabel = text.split('/')[0].trim();
  return shortLabel || text;
};

export const buildAttachmentViewerPageUrl = (fileUrl, displayName = 'Document') => {
  if (!fileUrl) return null;
  if (/^data:/i.test(fileUrl) || /^blob:/i.test(fileUrl)) return fileUrl;

  const viewerTitle = getAttachmentViewerTitle(displayName);

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(fileUrl, base);
    parsed.searchParams.delete('embed');
    parsed.searchParams.set('title', viewerTitle);
    return parsed.toString();
  } catch {
    let openUrl = String(fileUrl)
      .replace(/([?&])embed=1(?=&|$)/g, '$1')
      .replace(/[?&]$/, '');
    const separator = openUrl.includes('?') ? '&' : '?';
    openUrl = openUrl.replace(/([?&])title=[^&]*/g, '');
    return `${openUrl}${separator}title=${encodeURIComponent(viewerTitle)}`;
  }
};

export const buildAttachmentEmbedUrl = (filename, displayName = '') => {
  const baseUrl = buildAttachmentUrl(filename, displayName);
  if (!baseUrl) return null;
  if (/^data:/i.test(baseUrl) || /^blob:/i.test(baseUrl)) return baseUrl;
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}embed=1`;
};

export const getAttachmentFilenameFromUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('inline:')) return '';

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const fileParam = parsed.searchParams.get('file');
    if (fileParam) return decodeURIComponent(fileParam);
  } catch {
    // ignore malformed URLs
  }

  const withoutHash = String(url).split('#')[0];
  const withoutQuery = withoutHash.split('?')[0];
  return withoutQuery.split(/[/\\]/).pop() || '';
};

const hasAttachmentExtension = (value, extensions) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  const pattern = new RegExp(`\\.(${extensions.join('|')})(?:$|[?&#])`, 'i');
  return pattern.test(normalized);
};

export const isAttachmentImageUrl = (url) => {
  if (/^data:image\//i.test(url)) return true;
  const filename = getAttachmentFilenameFromUrl(url);
  const source = filename || url;
  return hasAttachmentExtension(source, ['jpe?g', 'png', 'gif', 'webp', 'bmp', 'svg']);
};

export const isAttachmentPdfUrl = (url) => {
  if (/^data:application\/pdf/i.test(url)) return true;
  const filename = getAttachmentFilenameFromUrl(url);
  const source = filename || url;
  return hasAttachmentExtension(source, ['pdf']);
};

export const getAttachmentPreviewKind = (url) => {
  if (isAttachmentImageUrl(url)) return 'image';
  if (isAttachmentPdfUrl(url)) return 'pdf';
  return 'document';
};

export const verifyAttachmentUrl = async (url) => {
  if (!url || /^data:/i.test(url) || /^blob:/i.test(url)) return true;
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok || response.status === 206;
  } catch {
    try {
      const response = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-0' } });
      return response.ok || response.status === 206;
    } catch {
      return false;
    }
  }
};

const buildViewerDocument = (fileUrl, displayName) => {
  const title = String(displayName).trim() || 'Document';
  const safeTitle = escapeHtml(title);
  const safeUrl = escapeHtml(fileUrl);
  const faviconDataUri = escapeHtml(getHelportMarkFaviconDataUri());
  const markSvg = HELPORT_MARK_SVG;
  const isImage = /^data:image\//i.test(fileUrl)
    || /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(fileUrl);
  const isPdf = /^data:application\/pdf/i.test(fileUrl)
    || /\.pdf(\?|$)/i.test(fileUrl);

  let content = '';
  if (isImage) {
    content = `<div class="attachment-viewer-content"><img src="${safeUrl}" alt="${safeTitle}" /></div>`;
  } else if (isPdf) {
    content = `<iframe class="attachment-viewer-frame" src="${safeUrl}" title="${safeTitle}"></iframe>`;
  } else {
    content = `<iframe class="attachment-viewer-frame" src="${safeUrl}" title="${safeTitle}"></iframe>`;
  }

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${safeTitle}</title><link rel="icon" type="image/svg+xml" href="${faviconDataUri}"><link rel="shortcut icon" type="image/svg+xml" href="${faviconDataUri}"><style>${VIEWER_STYLES}</style></head><body><header class="attachment-viewer-header"><span class="attachment-viewer-header__mark" aria-hidden="true">${markSvg}</span><span>${safeTitle}</span></header>${content}</body></html>`;
};

const openHtmlViewerWindow = (fileUrl, displayName) => {
  const viewer = window.open('about:blank', '_blank');
  if (!viewer) return false;

  try {
    viewer.document.open();
    viewer.document.write(buildViewerDocument(fileUrl, displayName));
    viewer.document.close();
    viewer.opener = null;
    return true;
  } catch {
    try {
      viewer.close();
    } catch {
      // ignore close errors
    }
    return false;
  }
};

export const openAttachmentViewer = (fileUrl, displayName = 'Document') => {
  if (!fileUrl) return false;

  const viewerTitle = getAttachmentViewerTitle(displayName);

  if (/^data:/i.test(fileUrl) || /^blob:/i.test(fileUrl)) {
    if (openHtmlViewerWindow(fileUrl, viewerTitle)) return true;
    window.open(fileUrl, '_blank');
    return true;
  }

  const openUrl = buildAttachmentViewerPageUrl(fileUrl, viewerTitle);
  window.open(openUrl, '_blank', 'noopener,noreferrer');
  return true;
};
